import React from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { useDispatch, useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { Card, Button } from 'react-bootstrap';
import { object, ref, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form, FormikHelpers, useFormikContext } from 'formik';

import { UserAvatar } from '@components/Feature/User';
import { ServerError } from '@components/Helper';
import { useRouteParam } from '@hooks/params';
import { FormGroupControl } from '@components/Base';
import { AuthOtpTokenPasswordUpdateParams } from '@apis/OtpApi';

import { otpUserFetch, OtpUserFetchPayload, passwordUpdate, PasswordUpdatePayload } from '../action';
import { selectAuthRouteReducer } from '../selector';

type FormValues = AuthOtpTokenPasswordUpdateParams;

const GROUP_PROPS = { className: 'mb-3' };

const FormForm = () => {
  const { t } = useTranslation();
  const { serverErrors } = useSelector(selectAuthRouteReducer);
  const { isSubmitting, isValid } = useFormikContext<FormValues>();

  return (
    <Form>
      <FormGroupControl type="hidden" name="otpToken" groupProps={GROUP_PROPS} />

      <FormGroupControl
        required
        type="password"
        name="password"
        label={t('common:password')}
        groupProps={GROUP_PROPS}
      />

      <FormGroupControl
        required
        type="password"
        name="passwordConfirmation"
        label={`${t('common:confirm')} ${t('common:password')}`}
        groupProps={GROUP_PROPS}
      />

      <Button type="submit" disabled={!isValid || isSubmitting}>
        {`${t('common:update')} ${t('common:password')}`}
      </Button>

      <ServerError errors={serverErrors} />
    </Form>
  );
}

const OtpTokenPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector(selectAuthRouteReducer);
  const otpToken = useRouteParam('otpToken')!;

  const initialValues: FormValues = {
    otpToken,
    password: '',
    passwordConfirmation: '',
  };

  const validationSchema = object({
    password: string()
      .min(6, t('validation:min', { min: 6 }))
      .required(t('validation:required')),
    passwordConfirmation: string()
      .oneOf([ref('password'), null], t('validation:inclusion'))
      .required(t('validation:required')),
  });

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: PasswordUpdatePayload = values;
    dispatch(passwordUpdate(payload));
    setSubmitting(false);
  }

  useEffectOnce(() => {
    const payload: OtpUserFetchPayload = { otpToken };
    dispatch(otpUserFetch(payload));
  });

  return (
    <Card>
      <Card.Header className="text-center">
        <UserAvatar />
      </Card.Header>
      <Card.Body>
        <Card.Title>
          {<Trans i18nKey="otp.updatePasswordFor" values={{ mobileNumber: user?.mobileNumber }} />}
        </Card.Title>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <FormForm />
        </Formik>
      </Card.Body>
    </Card>
  );
};

export { OtpTokenPage };
